import {
        FaDatabase,
        FaFilter,
        FaSortAmountDown,
        FaLayerGroup,
        FaCalculator,
        FaTable,
        FaFingerprint,
        FaLessThanEqual,
        FaSearch,
        FaStream,
        FaUserFriends
} from 'react-icons/fa';

export const sqlFundamentalsMetadata = {
        id: 'sql-fundamentals',
        title: "SQL Fundamentals",
        company: "DataHelix Academy",
        category: "SQL Foundations",
        cardDescription: "Master the fundamentals of SQL by querying a real e-commerce database. Learn to speak the language of data from scratch.",
        learningObjectives: [
                "Basic Queries (SELECT, FROM)",
                "Filtering & Logic (WHERE, AND, OR)",
                "Sorting & Limiting (ORDER BY, LIMIT)",
                "Aggregations (COUNT, GROUP BY)",
                "Basic Relationships (INNER JOIN)"
        ],
        scenario: (
                <div className="space-y-8">
                        {/* Hero Section - Clean, Minimalist, Website Theme */}
                        <div className="bg-white dark:bg-[#0f172a] rounded-xl border border-gray-100 dark:border-gray-800 p-8 shadow-sm relative overflow-hidden group hover:border-orange-500/20 transition-all duration-500">
                                <div className="relative z-10">
                                        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                                                ShopMax <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">Analytics</span>
                                        </h3>

                                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg max-w-3xl font-light">
                                                You have joined as a <strong className="text-gray-900 dark:text-white font-semibold">Data Analyst</strong> at a rapidly growing e-commerce startup.
                                                The CEO needs immediate answers about user growth, order volume, and sales performance to drive the next round of funding.
                                        </p>
                                </div>
                                {/* Subtle Brand Accent */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-red-500/5 rounded-full blur-2xl -mr-10 -mt-10"></div>
                        </div>

                        {/* Schema Grid - Strictly Professional, No Emojis */}
                        <div>
                                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4 pl-1">
                                        Data Architecture
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {[
                                                { table: 'users', desc: 'Customer Accounts' },
                                                { table: 'products', desc: 'Inventory Catalog' },
                                                { table: 'orders', desc: 'Transaction Log' }
                                        ].map((item) => (
                                                <div key={item.table} className="bg-gray-50 dark:bg-[#162032] p-4 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                                                        <div className="font-mono font-bold text-orange-600 dark:text-orange-400 text-sm mb-1">
                                                                {item.table}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                {item.desc}
                                                        </div>
                                                </div>
                                        ))}
                                </div>
                        </div>
                </div>
        ),
        introduction: ``,
        hasSQL: true
};

export const sqlFundamentalsChallenges = [
        {
                id: 'select-star',
                title: 'SELECT - Your First Query',
                question: 'Retrieve all columns from the users table',
                keyConcepts: ['SELECT', 'FROM', '*', 'Retrieving Data'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'user_id', type: 'INTEGER' },
                        { name: 'signup_date', type: 'DATE' },
                        { name: 'country', type: 'TEXT' },
                        { name: 'device_type', type: 'TEXT' },
                        { name: 'acquisition_channel', type: 'TEXT' },
                ],
                taskCallout: {
                        code: 'SELECT * FROM users',
                        description: 'This query will return all users and every column of information we have on them.',
                },
                businessRequest: `The Marketing Team is launching a new **"Welcome Series"** email campaign and needs a raw export of our entire user base to import into their email tool - every single data point we have on our users.`,
                concept: `**The SELECT Statement**

The \`SELECT\` statement is your primary tool for retrieving data. Think of SQL keywords like \`SELECT\` and \`FROM\` as the verbs of the language, and the column or table names as the nouns.

\`\`\`sql
SELECT * FROM table_name
\`\`\`

- \`SELECT *\` - The asterisk is a wildcard meaning *all columns*.
- \`FROM users\` - Specifies which table to pull data from.

*Note: In production, selecting everything is often inefficient. But for initial exploration and understanding data structure, it is the standard starting point.*`,
                context: `**The Business Request**

The Marketing Team is launching a new "Welcome Series" campaign. They need a raw export of our entire user base to import into their email tool. They requested *every single data point* we have on our users.

**The Concept**

The \`SELECT\` statement is your primary tool for retrieving data.
Values like columns and tables are the "nouns" of SQL, while keywords like SELECT are the "verbs".

**The Syntax**

\`\`\`sql
SELECT * FROM table_name
\`\`\`

- \`SELECT *\`: The asterisk is a wildcard meaning "all columns".
- \`FROM users\`: Specifies the target table.

<em>Note: In production, selecting <strong>everything</strong> is often inefficient. But for initial exploration, it is the standard starting point.</em>

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Type this and run:

\`\`\`sql
SELECT * FROM users
\`\`\`

This query will show **all users** and **all their information**.

</div>`,
                hint: `Start with \`SELECT *\` to grab everything.
Then specify which table you need with \`FROM\`.`,
                solution: `SELECT * FROM users`,
                explanation: `**🎯 Your First Query Executed!**

**What Just Happened:**

You told the database: "Give me ALL columns (\`*\`) FROM the users table." The database scanned the entire table and returned every row.

**Business Context:**

This is how analysts start exploring unfamiliar data:
- "What columns exist?" → Run \`SELECT *\` to see the structure
- "What does the data look like?" → Examine the first few rows
- "Is there anything weird?" → Spot nulls, unexpected values

**The SELECT Statement Components:**

| Keyword | Role | Purpose |
|---------|------|---------|
| \`SELECT\` | The Verb | Action to perform ("Get me...") |
| \`*\` | The Subject | Attributes to get ("Everything") |
| \`FROM\` | The Context | Where to look ("...from users") |

\`\`\`sql
SELECT [what columns]
FROM [which table]
\`\`\`

**Pro Tip:** In production, avoid \`SELECT *\` for scheduled jobs. Always list specific columns to prevent breakage when schema changes.`,
                concepts: [
                        {
                                icon: <FaDatabase className="text-3xl text-blue-600" />,
                                title: 'SELECT',
                                description: 'The fundamental command to fetch data from a database.'
                        }
                ]
        },
        {
                id: 'select-specific',
                title: 'SELECT Specific Columns',
                question: 'Write a query that returns ONLY the `user_id` and `country` for all users. Do not include any other columns.',
                keyConcepts: ['Column Selection', 'Bandwidth Optimization'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'user_id', type: 'INTEGER' },
                        { name: 'country', type: 'TEXT' },
                ],
                businessRequest: `The Analytics VP is building a **"Geographic Distribution"** report and only needs the user identifier and country for every customer. They explicitly said: *"I don't need emails or names - just where our users are located to plan our server expansion."*`,
                concept: `**Selecting Specific Columns**

Instead of \`SELECT *\`, you can list exactly which columns you need. This makes your queries faster and your results easier to read.

\`\`\`sql
SELECT column1, column2
FROM table_name
\`\`\`

- Only the columns you name are returned - nothing else.
- Separate multiple column names with commas.
- The order you list columns controls the order they appear in the output.

*In production systems, listing specific columns is strongly preferred. Cloud warehouses like BigQuery and Snowflake charge by the amount of data scanned - selecting fewer columns literally saves money.*`,
                context: `**The Business Request**

The Analytics VP wants a "Geographic Distribution" report.
They explicitly stated: <em>"I don't need emails or names, I just need to know where our users are located to plan our server expansion."</em>

**The Concept**

Fetching unnecessary columns wastes network bandwidth and memory.
Instead of \`*\`, we lists exactly what we need.

**The Syntax**

\`\`\`sql
SELECT column1, column2 
FROM table_name
\`\`\`

**Why this matters:**
- **Performance**: Less data to transfer.
- **Security**: Reduces risk of exposing PII (Personally Identifiable Information).

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Write a query that returns ONLY the \`user_id\` and \`country\` for all users. Do not include any other columns.

</div>`,
                hint: `Instead of \`*\`, list the exact column names separated by commas.
Don't forget the \`FROM\` clause!`,
                solution: `SELECT user_id, country FROM users`,
                explanation: `**🎯 Precision Achieved!**

**What Just Happened:**

Instead of \`*\` (all columns), you specified exactly which columns to return. The database only fetched those two, ignoring everything else.

**Business Context:**

Selecting specific columns matters for:
- **Privacy**: Don't expose \`email\` or \`phone\` when they're not needed
- **Performance**: Less data transferred = faster queries on large tables
- **Clarity**: Stakeholders see only relevant information

**How Column Selection Works:**

| Syntax | Returns | Efficiency |
|--------|---------|------------|
| \`SELECT *\` | All columns | ❌ Expensive |
| \`SELECT col1, col2\` | Only specified columns | ✅ Efficient |

**Pro Tip:** Cloud data warehouses (BigQuery, Snowflake) charge by data scanned. Selecting fewer columns literally saves money.`,
                concepts: [
                        {
                                icon: <FaStream className="text-3xl text-green-500" />,
                                title: 'Efficiency',
                                description: 'Selecting only necessary data reduces I/O and improves performance.'
                        }
                ]
        },
        {
                id: 'distinct-values',
                title: 'DISTINCT - Removing Duplicates',
                question: 'Generate a clean list of all unique `acquisition_channel` values found in the `users` table.',
                keyConcepts: ['DISTINCT', 'Deduplication'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'acquisition_channel', type: 'TEXT' },
                ],
                businessRequest: `The Product Team is redesigning the **"How did you hear about us?"** dropdown in the signup flow. They need a list of all unique marketing channels currently stored in the database to ensure the new options match historical data.`,
                concept: `**The DISTINCT Keyword**

\`DISTINCT\` removes duplicate values and returns only unique entries from a column.

\`\`\`sql
SELECT DISTINCT column_name
FROM table_name
\`\`\`

- Without \`DISTINCT\`: every row is returned, including many repeats.
- With \`DISTINCT\`: each unique value appears exactly once.
- Useful for exploring what values a column contains - e.g. checking possible statuses, channels, or categories.

*\`DISTINCT\` can be slow on very large tables because the database must scan and compare every value. Use it deliberately.*`,
                context: `**The Business Request**

The Product Team is redesigning the "How did you hear about us?" dropdown in the signup flow.
They need a list of all **unique** marketing channels currently in our database to ensure the new dropdown options match historical data.

**The Concept**

If you run a normal SELECT, you will see duplicates (e.g., "Google", "Google", "Google"). This is noise.
The \`DISTINCT\` keyword forces the database to remove duplicates before returning results.

**The Syntax**

\`\`\`sql
SELECT DISTINCT column_name
FROM table_name
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Generate a clean list of all unique \`acquisition_channel\` values found in the \`users\` table.

</div>`,
                hint: `Use \`SELECT DISTINCT\` followed by the column name \`acquisition_channel\`.`,
                solution: `SELECT DISTINCT acquisition_channel FROM users`,
                explanation: `**🎯 Unique Values Only!**

**What Just Happened:**

\`DISTINCT\` scanned the entire column and removed duplicate values. Instead of thousands of rows saying "Google, Google, Facebook, Google...", you get a clean list: "Google, Facebook, Organic".

**Business Context:**

\`DISTINCT\` powers everyday tasks:
- Populating dropdown filters in dashboards
- Data quality checks: "Do we have unexpected values?"
- Counting unique items: \`COUNT(DISTINCT user_id)\`

**How DISTINCT Works:**

| Raw Data (Duplicates) | DISTINCT Result (Unique) |
|-----------------------|--------------------------|
| Google | Google |
| Google | Facebook |
| Facebook | Organic |
| Google | |
| Organic | |

**Warning:** \`DISTINCT\` can be slow on large datasets because it must compare every value. Use it purposefully.`,
                concepts: [
                        {
                                icon: <FaFingerprint className="text-3xl text-purple-500" />,
                                title: 'DISTINCT',
                                description: 'Removes duplicate rows to return only unique values.'
                        }
                ]
        },
        {
                id: 'where-clause',
                title: 'WHERE - Filter Rows',
                question: 'Retrieve all columns for users where `country` is exactly `\'US\'`.',
                keyConcepts: ['WHERE', 'Filtering', 'Predicates'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'user_id', type: 'INTEGER' },
                        { name: 'signup_date', type: 'DATE' },
                        { name: 'country', type: 'TEXT' },
                        { name: 'device_type', type: 'TEXT' },
                        { name: 'acquisition_channel', type: 'TEXT' },
                ],
                businessRequest: `The **US Sales Team** is launching a regional promotion and needs a targeted list of users located in the United States. Users from all other countries must be excluded from the export.`,
                concept: `**The WHERE Clause**

\`WHERE\` filters your results, keeping only the rows that match your condition and discarding the rest.

\`\`\`sql
SELECT columns
FROM table_name
WHERE column_name = 'Value'
\`\`\`

- Every row is tested against the condition. Rows that pass (**TRUE**) are kept; rows that fail (**FALSE**) are discarded.
- Text values must be wrapped in single quotes: \`'US'\`, \`'active'\`.
- Numbers do not need quotes: \`amount > 100\`.

*\`WHERE\` runs before \`SELECT\`, so you can filter on columns you don't even display in the output.*`,
                context: `**The Business Request**

The US Sales Team is launching a regional promotion.
They need a targeted list of users who are located in the United States ('US') to load into their CRM. Users from other regions should be excluded.

**The Concept**

The \`WHERE\` clause acts as a filter. It evaluates every row and asks:
- True? Keep the row.
- False? Discard it.

**The Syntax**

\`\`\`sql
SELECT columns
FROM table_name
WHERE column_name = 'Value'
\`\`\`

**Critical Detail:** Text values must be wrapped in single quotes (e.g., \`'US'\`).

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Retrieve all information for users where the \`country\` is exactly \`'US'\`.

</div>`,
                hint: `You need a \`WHERE\` clause that checks if \`country\` is equal to \`'US'\`. Don't forget the single quotes.`,
                solution: `SELECT * FROM users WHERE country = 'US'`,
                explanation: `**🎯 Filtered Successfully!**

**What Just Happened:**

The \`WHERE\` clause told the database: "Only return rows where country equals 'US'." Every other row was excluded before the results came back.

**Business Context:**

Filtering is the heart of targeted analysis:
- Regional campaigns: \`WHERE country = 'US'\`
- Active users only: \`WHERE status = 'active'\`
- Recent data: \`WHERE created_at > '2024-01-01'\`

**How WHERE Works:**

\`WHERE\` evaluates each row against your condition. Only rows where the condition is \`TRUE\` are included in the output.

**Syntax Rules for Values:**

| Data Type | Needs Quotes? | Example |
|-----------|---------------|---------|
| **Text** | ✅ YES | \`'US'\`, \`'active'\` |
| **Date** | ✅ YES | \`'2024-01-01'\` |
| **Number** | ❌ NO | \`100\`, \`42.5\` |
| **Boolean** | ❌ NO | \`TRUE\` |
| **Column** | ❌ NO | \`country\`, \`amount\` |

**Pro Tip:** \`WHERE\` runs before \`SELECT\`, so you can filter on columns you don't even display.`,
                concepts: [
                        {
                                icon: <FaFilter className="text-3xl text-red-500" />,
                                title: 'WHERE',
                                description: 'Filters records based on specific conditions.'
                        }
                ]
        },
        {
                id: 'where-comparison',
                title: 'Comparison Operators',
                question: 'Find all rows in the `payments` table where `amount` is strictly greater than `100`.',
                keyConcepts: ['>', '<', '>=', 'Operators'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'payment_id', type: 'INTEGER' },
                        { name: 'order_id', type: 'INTEGER' },
                        { name: 'payment_date', type: 'DATE' },
                        { name: 'payment_method', type: 'TEXT' },
                        { name: 'payment_status', type: 'TEXT' },
                        { name: 'amount', type: 'DECIMAL' },
                ],
                businessRequest: `The **"VIP Customer"** programme automatically flags big spenders. We define a high-value transaction as any payment strictly over $100. The Support Team needs a live feed of these to send personal thank-you notes.`,
                concept: `**Comparison Operators**

SQL supports mathematical logic beyond simple equality. You can filter rows using a full range of comparison operators.

\`\`\`sql
SELECT * FROM table
WHERE amount > 100
\`\`\`

- \`>\` - Greater than
- \`<\` - Less than
- \`>=\` - Greater than or equal
- \`<=\` - Less than or equal
- \`<>\` or \`!=\` - Not equal
- \`=\` - Equal

*Numbers do not need quotes. Text and date values always require single quotes.*`,
                context: `**The Business Request**

The "VIP Customer" program automatically flags big spenders.
We define a "High Value Transaction" as any payment over $100. The Support Team needs a live feed of these transactions to send personal "Thank You" notes.

**The Concept**

SQL isn't just for checking equality (matches). It supports full mathematical logic.

**The Syntax**

\`\`\`sql
SELECT * FROM table
WHERE amount > 500
\`\`\`

**Standard Operators:**
- \`>\` (Greater than)
- \`<\` (Less than)
- \`>=\` (Greater or Equal)
- \`<>\` (Not Equal)

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Identify all rows in the \`payments\` table where the \`amount\` is **strictly greater than** 100.

</div>`,
                hint: `Use the greater than symbol (\`>\`) to filter the \`amount\` column. Numbers do *not* need quotes.`,
                solution: `SELECT * FROM payments WHERE amount > 100`,
                explanation: `**🎯 Comparison Mastered!**

**What Just Happened:**

The \`>\` operator compared each row's \`amount\` to 100. Only rows where amount was greater than 100 passed the filter.

**Business Context:**

Comparison operators drive business logic:
- High-value transactions: \`amount > 1000\`
- Struggling accounts: \`balance < 0\`
- Exact matches: \`status = 'approved'\`

**Comparison Operators:**

| Operator | Meaning | Example |
|----------|---------|---------|
| \`=\` | **Equal to** | \`status = 'active'\` |
| \`>\` | **Greater than** | \`amount > 100\` |
| \`<\` | **Less than** | \`age < 18\` |
| \`>=\` | **Greater or equal** | \`score >= 90\` |
| \`<=\` | **Less or equal** | \`quantity <= 0\` |
| \`<>\` or \`!=\` | **Not equal** | \`status <> 'cancelled'\` |

**Pro Tip:** Use \`BETWEEN\` for ranges: \`WHERE amount BETWEEN 100 AND 500\`.`,
                concepts: [
                        {
                                icon: <FaLessThanEqual className="text-3xl text-orange-500" />,
                                title: 'Operators',
                                description: 'Mathematical symbols used to compare values in a filter.'
                        }
                ]
        },
        {
                id: 'order-by',
                title: 'ORDER BY - Sort Results',
                question: 'Select all payments and sort them so the highest `amount` values appear at the top.',
                keyConcepts: ['ORDER BY', 'ASC', 'DESC'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'payment_id', type: 'INTEGER' },
                        { name: 'order_id', type: 'INTEGER' },
                        { name: 'payment_date', type: 'DATE' },
                        { name: 'payment_method', type: 'TEXT' },
                        { name: 'payment_status', type: 'TEXT' },
                        { name: 'amount', type: 'DECIMAL' },
                ],
                businessRequest: `The **Finance Director** is reviewing daily cash flow and wants to verify the largest transactions first. A scrambled list of payments is useless - they need the biggest amounts at the top of the report.`,
                concept: `**The ORDER BY Clause**

Databases store rows in no guaranteed order. \`ORDER BY\` enforces a specific sequence on your results.

\`\`\`sql
SELECT * FROM table_name
ORDER BY column_name DESC
\`\`\`

- \`ASC\` (Ascending) - Smallest to largest, A to Z. This is the **default** when no direction is specified.
- \`DESC\` (Descending) - Largest to smallest, Z to A.
- You can sort by multiple columns: \`ORDER BY country ASC, amount DESC\`.

*\`NULL\` values are treated differently across databases. In most, \`NULL\` appears last with \`ASC\` and first with \`DESC\`.*`,
                context: `**The Business Request**

The Finance Director is reviewing daily cash flow.
They want to see the largest transactions at the top of the report to verify them first. A scrambled list of payments is useless to them.

**The Concept**

Databases store data in random order. To enforce a specific sequence, you must use \`ORDER BY\`.

**The Syntax**

\`\`\`sql
SELECT * FROM table_name
ORDER BY column_name DESC
\`\`\`

- \`ASC\` (Ascending): Low to High (Default)
- \`DESC\` (Descending): High to Low

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Select all payments and sort them so the **highest amounts appear first**.

</div>`,
                hint: `Use \`ORDER BY amount DESC\` at the very end of your query.`,
                solution: `SELECT * FROM payments ORDER BY amount DESC`,
                explanation: `**🎯 Results Sorted!**

**What Just Happened:**

\`ORDER BY\` rearranged all rows based on the \`amount\` column. \`DESC\` (descending) put the highest values first.

**Business Context:**

Sorting transforms data into insights:
- Leaderboards: Highest scores first
- Recent activity: Newest dates first
- Price comparison: Cheapest first

**How ORDER BY Works:**

| Direction | Keyword | Example |
|-----------|---------|---------|
| **Ascending** (Default) | \`ASC\` or omit | 1, 2, 3... or A, B, C... |
| **Descending** | \`DESC\` | 3, 2, 1... or Z, Y, X... |

**Sorting Multiple Columns:**

\`ORDER BY country, amount DESC\` sorts by country first, then within each country, by amount (highest first).

**Pro Tip:** \`NULL\` values sort differently by database. In most, \`NULL\` comes last with \`ASC\` and first with \`DESC\`.`,
                concepts: [
                        {
                                icon: <FaSortAmountDown className="text-3xl text-indigo-500" />,
                                title: 'ORDER BY',
                                description: 'Sorts the result set by one or more columns.'
                        }
                ]
        },
        {
                id: 'limit-results',
                title: 'LIMIT - Top N Results',
                question: 'Retrieve exactly the top 5 highest payments - sort by `amount` descending, then limit the results to 5 rows.',
                keyConcepts: ['LIMIT', 'Top-N Analysis'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'payment_id', type: 'INTEGER' },
                        { name: 'order_id', type: 'INTEGER' },
                        { name: 'payment_date', type: 'DATE' },
                        { name: 'payment_method', type: 'TEXT' },
                        { name: 'payment_status', type: 'TEXT' },
                        { name: 'amount', type: 'DECIMAL' },
                ],
                businessRequest: `We are building a **"Recent Big Wins"** dashboard widget. The widget is small and can only display 5 entries. Fetching thousands of rows would be unnecessarily expensive - we need just the top 5 largest payments.`,
                concept: `**The LIMIT Clause**

\`LIMIT\` restricts the number of rows returned by a query. Combined with \`ORDER BY\`, it creates **Top N** rankings.

\`\`\`sql
SELECT * FROM table
ORDER BY value DESC
LIMIT 5
\`\`\`

- Place \`LIMIT\` at the very end of the query.
- \`ORDER BY\` without \`LIMIT\` returns all sorted rows.
- \`LIMIT\` without \`ORDER BY\` returns random rows - rarely useful.

*Other databases use different syntax: SQL Server uses \`TOP 5\`, Oracle uses \`FETCH FIRST 5 ROWS ONLY\`.*`,
                context: `**The Business Request**

We are building a real-time dashboard widget called **"Recent Big Wins"**.
The widget is small; it can only display the top 5 largest payments. Fetching thousands of rows would crash the browser.

**The Concept**

When viewing massive tables, you rarely want <strong>all</strong> the rows. You combine sorting and limiting to find the "Top N" records.

**The Syntax**

\`\`\`sql
SELECT * FROM table
ORDER BY value DESC
LIMIT 10
\`\`\`

This pattern (Order + Limit) is how you build **Leaderboards**.

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Retrieve exactly the top 5 highest payments.
1. Sort by amount (descending)
2. Limit the result to 5 rows

</div>`,
                hint: `Combine \`ORDER BY\` (to sort by amount) and \`LIMIT\` (to restrict the count) to get exactly the top 5.`,
                solution: `SELECT * FROM payments ORDER BY amount DESC LIMIT 5`,
                explanation: `**🎯 Top N Query Built!**

**What Just Happened:**

\`ORDER BY amount DESC\` sorted all payments from highest to lowest, then \`LIMIT 5\` returned only the first 5 rows from that sorted list.

**Business Context:**

Top-N queries are everywhere:
- "Top 5 customers by revenue" for account management
- "Top 10 products this week" for homepage features
- "Top 3 errors today" for engineering alerts

**The Pattern:**

\`\`\`sql
SELECT ...
FROM ...
ORDER BY [ranking column] DESC
LIMIT [how many]
\`\`\`

**Important:** \`LIMIT\` without \`ORDER BY\` returns random rows-not very useful. Always pair them for meaningful results.

**Database Variations:**

| Database | Syntax |
|----------|--------|
| **PostgreSQL, MySQL** | \`LIMIT 5\` |
| **SQL Server** | \`TOP 5\` |
| **Oracle** | \`FETCH FIRST 5 ROWS ONLY\` |`,
                concepts: [
                        {
                                icon: <FaTable className="text-3xl text-teal-500" />,
                                title: 'LIMIT',
                                description: 'Restricts the number of rows returned by the query.'
                        }
                ]
        },
        {
                id: 'null-check',
                title: 'Filtering Text',
                question: 'Find all records in the `orders` table where `order_status` is `\'cancelled\'`.',
                keyConcepts: ['IS NOT NULL', 'Status Checks'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'order_id', type: 'INTEGER' },
                        { name: 'user_id', type: 'INTEGER' },
                        { name: 'order_date', type: 'DATE' },
                        { name: 'order_status', type: 'TEXT' },
                ],
                businessRequest: `The **Customer Support Team** needs to audit our cancellation logic. They need a list of all orders currently marked as \`'cancelled'\` to verify that refunds were processed correctly for each one.`,
                concept: `**Filtering by Text Values**

Most business data is categorised using status columns - text values like \`'active'\`, \`'pending'\`, or \`'cancelled'\`. Filtering by these is one of the most frequent tasks in analytics.

\`\`\`sql
SELECT * FROM table
WHERE column = 'status_value'
\`\`\`

- The value must be an exact match, including case: \`'Cancelled'\` and \`'cancelled'\` are treated as different values.
- Run \`SELECT DISTINCT column\` first to see what values actually exist in the data.
- Use \`LOWER(column) = 'cancelled'\` if you suspect inconsistent casing.

*Status-based filtering drives everyday operations: refund processing, follow-up queues, revenue reporting, and error investigation.*`,
                context: `**The Business Request**

The Customer Support team needs to audit our "Cancellation Logic".
They need a list of all orders that are currently marked as 'cancelled' to ensure refunds were processed correctly.

**The Concept**

Most business workflows rely on **Status Columns** (e.g. 'pending', 'active', 'cancelled').
Filtering by these text tags is one of the most common analytical tasks.

**The Syntax**

\`\`\`sql
SELECT * FROM table 
WHERE column = 'status_value'
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Find all records in the \`orders\` table where the \`order_status\` is specifically **'cancelled'**.

</div>`,
                hint: `Use \`WHERE order_status = 'cancelled'\` on the \`orders\` table.`,
                solution: `SELECT * FROM orders WHERE order_status = 'cancelled'`,
                explanation: `**🎯 Status Filter Applied!**

**What Just Happened:**

You filtered rows where \`order_status\` exactly equals the text \`'cancelled'\`. Only those matching rows were returned.

**Business Context:**

Status-based filtering drives operations:
- \`'cancelled'\` → Refund processing
- \`'pending'\` → Follow-up queue
- \`'completed'\` → Revenue reporting
- \`'failed'\` → Error investigation

**Text Matching Gotchas:**

| Issue | Example | Solution |
|-------|---------|----------|
| **Case sensitivity** | \`'Cancelled' ≠ 'cancelled'\` | Use \`LOWER()\` |
| **Trailing spaces** | \`'cancelled ' ≠ 'cancelled'\` | \`TRIM()\` the column |
| **Typos** | \`'cancled'\` | Data quality check |

**Pro Tip:** Before writing a \`WHERE\`, run \`SELECT DISTINCT column\` to see what values actually exist.`,
                concepts: [
                        {
                                icon: <FaSearch className="text-3xl text-gray-500" />,
                                title: 'Status',
                                description: 'Filtering based on state columns is critical for business workflows.'
                        }
                ]
        },
        {
                id: 'aggregation-count',
                title: 'COUNT - Aggregation',
                question: 'Calculate the total number of users registered in the `users` table. Return a single number.',
                keyConcepts: ['COUNT', 'Aggregation'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'COUNT(*)', type: 'INTEGER' },
                ],
                businessRequest: `The **CEO** is preparing a pitch deck for investors. She needs to know the total number of registered users as of today - not a CSV file of names, just a single impressive number to put on a slide.`,
                concept: `**The COUNT Function**

Aggregation functions summarise many rows into a single value. \`COUNT\` is the most fundamental - it simply counts rows.

\`\`\`sql
SELECT COUNT(*) FROM table_name
\`\`\`

- \`COUNT(*)\` - Counts all rows, including those with NULL values.
- \`COUNT(column)\` - Counts only rows where that column is not NULL.
- \`COUNT(DISTINCT column)\` - Counts unique non-NULL values only.

*Use an alias to make the output label readable: \`SELECT COUNT(*) AS total_users\`.*`,
                context: `**The Business Request**

We are preparing a pitch deck for investors.
The CEO asks: <em>"What is our total registered user count as of today?"</em>
She doesn't want a CSV file of names; she needs a single, impressive number to put on a slide.

**The Concept**

Aggregations allow you to zoom out from *individual records* to *summary metrics*.
\`COUNT(*)\` is the most basic aggregation-it simply counts rows.

**The Syntax**

\`\`\`sql
SELECT COUNT(*) FROM table_name
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Calculate the total number of users registered in the database.

</div>`,
                hint: `Wrap the wildcard \`*\` inside the \`COUNT()\` function to get the total number of rows.`,
                solution: `SELECT COUNT(*) FROM users`,
                explanation: `**🎯 Your First Aggregation!**

**What Just Happened:**

\`COUNT(*)\` counted every row in the table and returned a single number. Instead of seeing individual users, you see their total.

**Business Context:**

Counting is the foundation of metrics:
- "Total users" → Growth tracking
- "Orders this month" → Sales performance
- "Active sessions" → System monitoring

**COUNT Variations:**

| Expression | Counts... |
|------------|-----------|
| \`COUNT(*)\` | All rows, including NULLs |
| \`COUNT(column)\` | Rows where column is NOT NULL |
| \`COUNT(DISTINCT col)\` | Unique non-NULL values |

**Example:**

If a table has 100 rows, but \`email\` is NULL for 10 of them:
- \`COUNT(*)\` → 100
- \`COUNT(email)\` → 90
- \`COUNT(DISTINCT country)\` → Number of unique countries

**Pro Tip:** Use column aliases for clarity: \`COUNT(*) AS total_users\`.`,
                concepts: [
                        {
                                icon: <FaCalculator className="text-3xl text-emerald-500" />,
                                title: 'Aggregation',
                                description: 'Functions like COUNT, SUM, and AVG that summarize multiple rows into a single value.'
                        }
                ]
        },
        {
                id: 'group-by',
                title: 'GROUP BY - Segmentation',
                question: 'Select the `country` and the count of users grouped by `country`. Show how many users exist in each location.',
                keyConcepts: ['GROUP BY', 'Segmentation'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'country', type: 'TEXT' },
                        { name: 'COUNT(*)', type: 'INTEGER' },
                ],
                businessRequest: `The count was great, but now the **CEO** asks: *"That's great, but where are they?"* We need to break down the total user count by country to guide the international marketing budget.`,
                concept: `**The GROUP BY Clause**

\`GROUP BY\` is the pivot table of SQL. It splits your data into buckets by a category, then applies an aggregation function to each bucket.

\`\`\`sql
SELECT category_col, COUNT(*)
FROM table
GROUP BY category_col
\`\`\`

SQL processes this in three steps:
1. **Split** - Divide rows into groups based on the column value.
2. **Apply** - Calculate \`COUNT\` (or \`SUM\`, \`AVG\`, etc.) within each group.
3. **Combine** - Return one summary row per group.

*The Golden Rule: every column in \`SELECT\` must either appear in \`GROUP BY\` or be wrapped in an aggregation function.*`,
                context: `**The Business Request**

The count was good, but now the CEO asks: *"That's great, but WHERE are they?"*
We need to break down that total user count by country to guide our international marketing budget.

**The Concept**

\`GROUP BY\` is the "Pivot Table" of SQL.
It splits your data into buckets (e.g., one bucket for 'US', one for 'UK'), counts the items in each bucket, and returns a summary.

**The Syntax**

\`\`\`sql
SELECT category_col, COUNT(*) 
FROM table 
GROUP BY category_col
\`\`\`

SQL will:
1. **Split** the table into groups based on the column
2. **Apply** the function (COUNT) to each group
3. **Combine** the results

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Generate a geographic breakdown.
Select the \`country\` and the count of users. Don't forget to \`GROUP BY country\`.

</div>`,
                hint: `Select the \`country\` and the \`COUNT(*)\`. Then, make sure to add \`GROUP BY country\` at the end.`,
                solution: `SELECT country, COUNT(*) FROM users GROUP BY country`,
                explanation: `**🎯 Segmented Analysis!**

**What Just Happened:**

\`GROUP BY\` split the users table into separate "buckets" by country. Then \`COUNT(*)\` counted the users within each bucket.

**Business Context:**

\`GROUP BY\` answers "How many/much per category?":
- Revenue per product category
- Users per signup source
- Tickets per support agent

**How GROUP BY Works:**

1. Database groups rows with identical \`country\` values
2. Applies \`COUNT(*)\` to each group independently
3. Returns one row per unique \`country\`

**The Golden Rule of Aggregation:**

Every column in your \`SELECT\` must be either (A) or (B):

| Column Type | Rule | Valid Example |
|-------------|------|---------------|
| **(A) Grouping Key** | Must be in \`GROUP BY\` | \`country\` |
| **(B) Metric** | Must be inside function | \`COUNT(*)\` |

**Invalid Example:**
\`SELECT country, name, COUNT(*) ...\`
❌ \`name\` is neither a group nor a metric. What does "name" mean for a whole country?

\`\`\`sql
SELECT country, COUNT(*) FROM users GROUP BY country
\`\`\`

**Pro Tip:** Add \`ORDER BY COUNT(*) DESC\` to see the biggest segments first.`,
                concepts: [
                        {
                                icon: <FaLayerGroup className="text-3xl text-yellow-500" />,
                                title: 'GROUP BY',
                                description: 'Groups rows that have the same values into summary rows.'
                        }
                ]
        },
        {
                id: 'basic-join',
                title: 'INNER JOIN - Connecting Tables',
                question: 'Join the `orders` and `users` tables - return each `order_id` alongside the `country` of the user who placed it.',
                keyConcepts: ['INNER JOIN', 'Foreign Keys', 'Relational Data'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'order_id', type: 'INTEGER' },
                        { name: 'country', type: 'TEXT' },
                ],
                businessRequest: `We suspect shipping costs are higher for international orders. To verify this, we need a single report showing every **Order ID** next to the **Country** of the customer who placed it - but those two pieces of data live in separate tables.`,
                concept: `**The INNER JOIN**

Relational databases split data across multiple tables to eliminate redundancy. \`JOIN\` reconnects them using a shared key column.

\`\`\`sql
SELECT a.column, b.column
FROM table_a a
JOIN table_b b ON a.foreign_key = b.primary_key
\`\`\`

- The \`ON\` clause specifies which columns to match across the two tables.
- \`INNER JOIN\` only returns rows where a match exists in **both** tables.
- If a row in table A has no match in table B, it is excluded from the result.

*Always prefix column names with the table name (or an alias) when working with JOINs to avoid ambiguity.*`,
                context: `**The Business Request**

We suspect shipping costs are higher for international orders.
To verify this, we need a single report that shows every **Order ID** next to the **Country** of the customer who placed it.
Problem: The Order ID is in the \`orders\` table, but the Country is in the \`users\` table.

**The Concept**

In a Relational Database, data is split to eliminate redundancy. To rebuild the full picture, you must **JOIN** tables together using a common key (usually an ID).

**The Syntax**

\`\`\`sql
SELECT tableA.col, tableB.col
FROM tableA
JOIN tableB ON tableA.id = tableB.foreign_id
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

1. Select \`orders.order_id\` and \`users.country\`.
2. JOIN the \`orders\` table with the \`users\` table.
3. Match them ON \`orders.user_id = users.user_id\`.

</div>`,
                hint: `Perform an \`INNER JOIN\` between \`orders\` and \`users\`. The logic is: \`ON orders.user_id = users.user_id\`. Select \`orders.order_id\` and \`users.country\`.`,
                solution: `SELECT orders.order_id, users.country
FROM orders
JOIN users ON orders.user_id = users.user_id`,
                explanation: `**🎯 Tables Connected!**

**What Just Happened:**

You combined two tables-\`orders\` and \`users\`-using their shared \`user_id\` column. Each order row gained access to its user's country.

**How Connections Work:**

| Component | Purpose | Example |
|-----------|---------|---------|
| **Source Table** | Where you start | \`FROM orders\` |
| **Target Table** | Info you need | \`JOIN users\` |
| **Connector** | Shared column | \`ON orders.user_id = users.user_id\` |

**Business Context:**

Joins answer cross-table questions:
- "Which country ordered the most?" (orders + users)
- "What genre did each user watch?" (sessions + content)
- "Which product sold best?" (orders + products)

**How INNER JOIN Works:**

1. Take each row from \`orders\`
2. Find matching row(s) in \`users\` where \`user_id\` matches
3. Combine the columns from both tables
4. If no match found, the order row is excluded

**The JOIN Syntax:**

\`\`\`sql
SELECT a.column, b.column
FROM table_a a
JOIN table_b b ON a.foreign_key = b.primary_key
\`\`\`

**Pro Tip:** Use table aliases (\`orders o\`, \`users u\`) to keep queries readable as you add more joins.`,
                concepts: [
                        {
                                icon: <FaUserFriends className="text-3xl text-pink-500" />,
                                title: 'JOIN',
                                description: 'Combines rows from two or more tables based on a related column between them.'
                        }
                ]
        },
        {
                id: 'capstone-fundamentals',
                title: 'Capstone: Order Volume Analysis',
                question: 'Count completed orders per `country` and `acquisition_channel`. Join `orders` and `users`, filter for `\'completed\'` status, group, and rank by highest volume first.',
                keyConcepts: ['INNER JOIN', 'WHERE', 'GROUP BY', 'ORDER BY'],
                defaultQuery: ``,

                expectedColumns: [
                        { name: 'country', type: 'TEXT' },
                        { name: 'acquisition_channel', type: 'TEXT' },
                        { name: 'total_orders', type: 'INTEGER' },
                ],
                businessRequest: `The **Operations Team** needs to optimise logistics and marketing spend. They want to know *where* (country) completed orders are coming from and *how* (acquisition channel) those customers found us - so they can double down on what works.`,
                concept: `**Putting It All Together**

This capstone combines five SQL clauses in a single query. This is the kind of query real analysts write every day.

\`\`\`sql
SELECT a.col, b.col, COUNT(a.id) AS total
FROM table_a a
JOIN table_b b ON a.id = b.id
WHERE a.status = 'completed'
GROUP BY a.col, b.col
ORDER BY total DESC
\`\`\`

SQL executes clauses in this order:
1. \`FROM\` / \`JOIN\` - Assemble all the data.
2. \`WHERE\` - Filter rows before grouping.
3. \`GROUP BY\` - Bucket the filtered rows.
4. \`SELECT\` - Apply aggregations to each bucket.
5. \`ORDER BY\` - Sort the final output.

*Understanding execution order helps you write correct queries and debug unexpected results.*`,
                context: `**The Final Business Request**

The Operations Team needs to optimize our logistics and marketing spend.
They need to know **where** (Country) our completed orders are coming from and **how** (Channel) those users found us.

**Requirements:**
1.  **Join**: Connect \`orders\` with \`users\` to link transactions to customer details.
2.  **Filter**: Only include orders where \`order_status = 'completed'\`.
3.  **Segment**: Group by both \`country\` and \`acquisition_channel\`.
4.  **Aggregate**: Count the total number of orders (\`COUNT(order_id)\`).
5.  **Rank**: Order by the highest volume first.

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Write a single query that answers this complex business question.

</div>`,
                hint: `**Step-by-Step Guide:**

1. **SELECT**: Start with the columns you need: \`users.country\`, \`users.acquisition_channel\`, and the count of orders \`COUNT(orders.order_id)\`.
2. **FROM**: Start from the \`orders\` table.
3. **JOIN**: Connect to the \`users\` table using \`ON orders.user_id = users.user_id\`.
4. **WHERE**: Filter for completed orders: \`orders.order_status = 'completed'\`.
5. **GROUP BY**: Group by the non-aggregated columns (\`country\` and \`acquisition_channel\`).
6. **ORDER BY**: Sort by the count in descending order.`,
                strictValidation: false,
                solution: `SELECT users.country, users.acquisition_channel, COUNT(orders.order_id) AS total_orders
FROM orders
JOIN users ON orders.user_id = users.user_id
WHERE orders.order_status = 'completed'
GROUP BY users.country, users.acquisition_channel
ORDER BY COUNT(orders.order_id) DESC`,
                explanation: `**🎯 Complete Analysis Delivered!**

**What Just Happened:**

You combined 5 SQL concepts in one query:
1. **JOIN** → Connected orders to user data
2. **WHERE** → Filtered for completed orders only
3. **GROUP BY** → Segmented by country AND channel
4. **COUNT** → Aggregated the volume
5. **ORDER BY** → Ranked by highest count

**Business Context:**

This query answers: "Where should we invest marketing budget?"
- High counts = successful channels worth scaling
- Low counts = opportunities to optimize or cut

**Skills You've Mastered:**

| Skill | What It Does |
|-------|-------------|
| SELECT | Choose columns |
| FROM | Specify tables |
| WHERE | Filter rows |
| JOIN | Connect tables |
| GROUP BY | Segment data |
| ORDER BY | Sort results |
| LIMIT | Cap output |
| Aggregations | Summarize values |

**Congratulations!** You now have the foundation for real data analysis work.`,
                concepts: [
                        {
                                icon: <FaLayerGroup className="text-3xl text-purple-600" />,
                                title: 'Complex Aggregation',
                                description: 'Grouping by multiple columns to find granular segments in your data.'
                        }
                ]
        }
];
